import type { AuditLogChange } from "@wingspanhq/wingspan-api-sdk";
import startCase from "lodash/startCase";
import { formatAttributeValue } from "./formatAttributeValue";

type Props = {
  changes: AuditLogChange[];
};

/**
 * `path` is an RFC 6901 JSON Pointer into the public resource representation
 * (e.g. `/status`, `/metadata/customerId`). v3 carries no display name for the
 * changed field, so the label is derived from the pointer segments.
 */
const formatChangeLabel = (path: string): string =>
  startCase(
    path
      .split("/")
      .filter(Boolean)
      .map(segment => segment.replace(/~1/g, "/").replace(/~0/g, "~"))
      .join(" ")
  );

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null;

const AttributeChangeItem = (props: { change: AuditLogChange }) => {
  const label = formatChangeLabel(props.change.path);
  const hasBefore = hasValue(props.change.before);
  const hasAfter = hasValue(props.change.after);

  return (
    <li>
      {label}
      <ul>
        <li>
          {hasBefore && (
            <span style={{ textDecoration: "line-through" }}>
              {formatAttributeValue(props.change.before)}
            </span>
          )}
          {hasBefore && hasAfter && " → "}
          {hasAfter && formatAttributeValue(props.change.after)}
          {/* v3 no longer flags redaction explicitly; an entry that discloses
              neither value is the case the `redacted` flag used to mark. */}
          {!hasBefore && !hasAfter && "Hidden"}
        </li>
      </ul>
    </li>
  );
};

export const AttributeChanges = (props: Props) => {
  if (props.changes.length === 0) return null;

  return (
    <ul>
      {props.changes.map((change, index) => (
        <AttributeChangeItem key={`${change.path}-${index}`} change={change} />
      ))}
    </ul>
  );
};
